import React, { useMemo } from 'react';
import {
  useLoadAutosave,
  useSaveAutosave,
  useDeleteAutosave,
} from 'queries/autosave/autosave';
import { useDebounce } from './useDebounce';

interface UseFormAutosaveOptions {
  key: string;
  data: Record<string, any>;
  delay?: number;
  enabled?: boolean;
  shouldSave?: boolean;
  onSaved?: (savedData: Record<string, any>) => void;
}

interface UseFormAutosaveResult {
  isSaving: boolean;
  clear: () => Promise<void>;
}

interface UseFormAutosaveLoadResult {
  loadedData: any;
  isLoading: boolean;
}
/**
 * Load Autosave
 *
 * @param key
 * @param enabled
 */
export const useFormAutosaveLoad = (
  key: string,
  enabled = true,
): UseFormAutosaveLoadResult => {
  const { data: loadedData, isLoading } = useLoadAutosave(key, enabled);

  return useMemo(() => ({ loadedData, isLoading }), [loadedData, isLoading]);
};

/**
 * Save Autosave
 */
export const useFormAutosave = ({
  key,
  data,
  delay = 2000,
  enabled = true,
  shouldSave = true,
  onSaved,
}: UseFormAutosaveOptions): UseFormAutosaveResult => {
  const debouncedPayload = useDebounce(
    {
      data,
      shouldSave,
      enabled,
    },
    delay,
  );

  //edit & create
  const saveMutation = useSaveAutosave();
  //delete
  const deleteMutation = useDeleteAutosave();

  React.useEffect(() => {
    const canSave =
      debouncedPayload.enabled &&
      debouncedPayload.shouldSave &&
      Boolean(debouncedPayload.data);

    if (!canSave) return;

    saveMutation.mutateAsync({ key, data: debouncedPayload.data }).then(() => {
      if (onSaved) onSaved(debouncedPayload.data);
    });
  }, [debouncedPayload, key, onSaved]);

  const clear = React.useCallback(async () => {
    await deleteMutation.mutateAsync(key);
  }, [key, deleteMutation]);

  return {
    isSaving: saveMutation.isLoading,
    clear,
  };
};
